import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { App, Button, Descriptions, Flex, Form, Input, InputNumber, Select, Tag, Typography } from 'antd';
import { useSetState } from 'ahooks';
import Permission, { useAccess } from '@/components/permission';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import AuditModal from '@/pages/mining-weekly-rewards/components/audit-modal';
import {
    formatAutoReleaseAt,
    formatDateTime,
    formatOutput,
    formatPeriodRange,
    getRewardPeriodLabel,
    getWeekPeriodLabel,
    isPendingStatus,
    toRewardNumber,
} from '@/pages/mining-weekly-rewards/utils';
import {
    miningWeeklyRewardApprove,
    miningWeeklyRewardApprovePeriod,
    miningWeeklyRewardExport,
    miningWeeklyRewardList,
    normalizeWeekGroup,
    type MiningWeeklyRewardItem,
    type MiningWeeklyRewardListParams,
    type MiningWeeklyRewardWeekGroup,
} from '@/services/mining-weekly-rewards';
import { resolveApiResult } from '@/utils/apiResponse';
import { handleExportResponse } from '@/utils/export-response';

import type { ProColumns } from '@ant-design/pro-components';
import type { PaginationProps, TableProps } from 'antd';

type TableRowSelection<T> = TableProps<T>['rowSelection'];

const PERMISSION = 'Dramas_Mining-Weekly-Rewards';
const REVIEW_PERMISSION = 'Dramas_Mining-Weekly-Rewards_Review';
const EXPORT_PERMISSION = 'Dramas_Mining-Weekly-Rewards_Export';
const LIST_PATH = '/dramas/mining-weekly-rewards';

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: 20,
};

type SearchFilters = Pick<MiningWeeklyRewardListParams, 'reward_type' | 'user_id'>;

type ModalState =
    | { open: false }
    | { open: true; mode: 'batch' }
    | { open: true; mode: 'period' };

type LocationState = {
    week?: MiningWeeklyRewardWeekGroup;
};

const pickSearchFilters = (values: Record<string, unknown>): SearchFilters => {
    const next: SearchFilters = {};
    (['user_id', 'reward_type'] as const).forEach((key) => {
        const text = String(values[key] ?? '').trim();
        if (text) next[key] = text;
    });
    return next;
};

const renderCopyableId = (value?: string | null) => {
    if (!value) return '-';
    return <Typography.Text copyable={{ text: value }}>{value}</Typography.Text>;
};

const PageMiningWeeklyRewardDetail: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const access = useAccess();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const weekNParam = searchParams.get('week_n');
    const weekN = weekNParam != null && weekNParam !== '' ? Number(weekNParam) : NaN;
    const locationWeek = (location.state as LocationState | null)?.week;

    const [weekOverview, setWeekOverview] = useState<MiningWeeklyRewardWeekGroup | undefined>(
        locationWeek ? normalizeWeekGroup(locationWeek as Record<string, any>) : undefined,
    );
    const [pagination, setPagination] = useSetState<PaginationProps>({ ...DEFAULT_PAGINATION });
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [dataSource, setDataSource] = useState<MiningWeeklyRewardItem[]>([]);
    const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [editedOutputs, setEditedOutputs] = useState<Record<string, number>>({});
    const [modal, setModal] = useState<ModalState>({ open: false });
    const [searchForm] = Form.useForm();
    const [exporting, setExporting] = useState(false);

    const canReview = access(REVIEW_PERMISSION);
    const canExport = access(EXPORT_PERMISSION);
    const hasValidWeek = Number.isFinite(weekN);

    const syncOverviewFromRows = useCallback((rows: MiningWeeklyRewardItem[]) => {
        if (!rows.length) return;
        setWeekOverview((prev) => {
            const sample = rows[0];
            const pending = rows.some((row) => isPendingStatus(row.audit_status));
            return {
                ...(prev || {}),
                week_n: sample.week_n,
                start_time: sample.start_time ?? prev?.start_time,
                end_time: sample.end_time ?? prev?.end_time,
                audit_status: pending ? 0 : (prev?.audit_status ?? sample.audit_status),
                audit_at: pending ? null : (prev?.audit_at ?? sample.audit_at),
                audit_by: pending ? null : (prev?.audit_by ?? sample.audit_by),
            };
        });
    }, []);

    const fetchList = useCallback(
        async (page: number, pageSize: number) => {
            if (!hasValidWeek) return;

            setLoading(true);
            try {
                const response = await miningWeeklyRewardList({
                    page,
                    size: pageSize,
                    week_n: weekN,
                    ...searchFilters,
                });
                const { data } = response;

                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                const meta = data.meta;
                const defaultPageSize = DEFAULT_PAGINATION.pageSize ?? 20;
                const nextPage = meta?.page ?? page;
                const nextPageSize = meta?.size ?? pageSize;
                const rows = (data.data ?? []).filter((item) => Number(item.week_n) === weekN);

                setPagination({
                    current:
                        typeof nextPage === 'number'
                            ? nextPage || 1
                            : Number(nextPage) > 0
                              ? Number(nextPage)
                              : 1,
                    pageSize:
                        typeof nextPageSize === 'number'
                            ? nextPageSize || defaultPageSize
                            : Number(nextPageSize) > 0
                              ? Number(nextPageSize)
                              : defaultPageSize,
                    total: meta?.total ?? rows.length,
                });
                setDataSource(rows);
                setSelectedRowKeys([]);
                setEditedOutputs({});
                syncOverviewFromRows(rows);
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || 'Failed to fetch detail');
            } finally {
                setLoading(false);
            }
        },
        [hasValidWeek, message, searchFilters, setPagination, syncOverviewFromRows, weekN],
    );

    const handleSearch = useCallback(() => {
        const nextFilters = pickSearchFilters(searchForm.getFieldsValue());
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters(nextFilters);
    }, [searchForm, setPagination]);

    const handleResetSearch = useCallback(() => {
        searchForm.resetFields();
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearchFilters({});
    }, [searchForm, setPagination]);

    const handleExport = useCallback(async () => {
        if (!hasValidWeek) {
            message.error(formatMessage({ id: 'miningWeeklyRewards.export.missingWeek' }));
            return;
        }
        setExporting(true);
        try {
            const { data } = await miningWeeklyRewardExport({ week_n: weekN });
            handleExportResponse(data, formatMessage({ id: 'miningWeeklyRewards.export.success' }));
        } catch (error: any) {
            message.error(error?.response?.data?.msg || error?.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    }, [formatMessage, hasValidWeek, message, weekN]);

    useEffect(() => {
        if (!hasValidWeek) {
            message.error(formatMessage({ id: 'miningWeeklyRewards.empty' }));
            navigate(LIST_PATH, { replace: true });
            return;
        }
        fetchList(pagination.current as number, pagination.pageSize as number);
    }, [fetchList]);

    const closeModal = useCallback(() => {
        setModal({ open: false });
    }, []);

    const openBatchModal = useCallback(() => {
        if (!selectedRowKeys.length) {
            message.warning(formatMessage({ id: 'miningWeeklyRewards.audit.noSelection' }));
            return;
        }
        setModal({ open: true, mode: 'batch' });
    }, [formatMessage, message, selectedRowKeys.length]);

    const openPeriodModal = useCallback(() => {
        const hasPending = dataSource.some((row) => isPendingStatus(row.audit_status));
        if (!hasPending && weekOverview && !isPendingStatus(weekOverview.audit_status)) {
            message.warning(formatMessage({ id: 'miningWeeklyRewards.audit.noPending' }));
            return;
        }
        setModal({ open: true, mode: 'period' });
    }, [dataSource, formatMessage, message, weekOverview]);

    const handleBatchApprove = useCallback(
        async ({ memo }: { memo?: string }) => {
            if (!selectedRowKeys.length) return;

            const selectedRows = dataSource.filter((row) => selectedRowKeys.includes(row.id));
            if (!selectedRows.length) return;

            const rewards = selectedRows.map((row) => {
                const rewardValue =
                    editedOutputs[row.id] !== undefined
                        ? editedOutputs[row.id]
                        : toRewardNumber(row.actual_output);
                return {
                    id: Number(row.id),
                    reward: rewardValue,
                    ...(memo?.trim() ? { memo: memo.trim() } : {}),
                };
            });

            setSubmitting(true);
            try {
                const { data } = await miningWeeklyRewardApprove({ rewards });
                const result = resolveApiResult(data);
                if (!result.success) {
                    message.error(result.msg || 'Approve failed');
                    return;
                }
                message.success(formatMessage({ id: 'c.operationCompleted' }));
                closeModal();
                await fetchList(pagination.current as number, pagination.pageSize as number);
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || 'Approve failed');
            } finally {
                setSubmitting(false);
            }
        },
        [
            closeModal,
            dataSource,
            editedOutputs,
            fetchList,
            formatMessage,
            message,
            pagination.current,
            pagination.pageSize,
            selectedRowKeys,
        ],
    );

    const handlePeriodApprove = useCallback(
        async ({ auditBy, auditRemark }: { auditBy?: string; auditRemark?: string }) => {
            if (!hasValidWeek) return;

            setSubmitting(true);
            try {
                const { data } = await miningWeeklyRewardApprovePeriod({
                    weekN,
                    ...(auditBy?.trim() ? { auditBy: auditBy.trim() } : {}),
                    ...(auditRemark?.trim() ? { auditRemark: auditRemark.trim() } : {}),
                });
                const result = resolveApiResult(data);
                if (!result.success) {
                    message.error(result.msg || 'Approve period failed');
                    return;
                }
                message.success(formatMessage({ id: 'c.operationCompleted' }));
                closeModal();
                await fetchList(pagination.current as number, pagination.pageSize as number);
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || 'Approve period failed');
            } finally {
                setSubmitting(false);
            }
        },
        [
            closeModal,
            fetchList,
            formatMessage,
            hasValidWeek,
            message,
            pagination.current,
            pagination.pageSize,
            weekN,
        ],
    );

    const rewardTypeOptions = useMemo(
        () => [
            {
                value: '0',
                label: formatMessage({ id: 'miningWeeklyRewards.rewardType.mining' }),
            },
            {
                value: '1',
                label: formatMessage({ id: 'miningWeeklyRewards.rewardType.invite' }),
            },
        ],
        [formatMessage],
    );

    const rowSelection = useMemo<TableRowSelection<MiningWeeklyRewardItem> | undefined>(
        () =>
            canReview
                ? {
                      selectedRowKeys,
                      preserveSelectedRowKeys: true,
                      onChange: (keys) => setSelectedRowKeys(keys),
                      getCheckboxProps: (record) => ({
                          disabled: !isPendingStatus(record.audit_status),
                      }),
                  }
                : undefined,
        [canReview, selectedRowKeys],
    );

    const columns = useMemo<ProColumns<MiningWeeklyRewardItem>[]>(
        () => [
            {
                dataIndex: 'index',
                title: 'No.',
                valueType: 'index',
                width: 60,
                fixed: 'left',
            },
            {
                dataIndex: 'user_id',
                title: <FormattedMessage id="miningWeeklyRewards.columns.userId" />,
                width: 200,
                render: (_, record) => renderCopyableId(record.user_id),
            },
            {
                dataIndex: 'reward_type',
                title: <FormattedMessage id="miningWeeklyRewards.columns.rewardType" />,
                width: 120,
                render: (_, record) => {
                    if (record.reward_type === 0) {
                        return formatMessage({ id: 'miningWeeklyRewards.rewardType.mining' });
                    }
                    if (record.reward_type === 1) {
                        return formatMessage({ id: 'miningWeeklyRewards.rewardType.invite' });
                    }
                    return record.reward_type ?? '-';
                },
            },
            {
                dataIndex: 'actual_output',
                title: <FormattedMessage id="miningWeeklyRewards.columns.actualOutput" />,
                width: 160,
                align: 'right',
                sorter: (a, b) => toRewardNumber(a.actual_output) - toRewardNumber(b.actual_output),
                render: (_, record) => {
                    const editable = canReview && isPendingStatus(record.audit_status);
                    if (!editable) {
                        return formatOutput(record.actual_output);
                    }
                    return (
                        <InputNumber
                            value={
                                editedOutputs[record.id] !== undefined
                                    ? editedOutputs[record.id]
                                    : toRewardNumber(record.actual_output)
                            }
                            min={0}
                            stringMode={false}
                            controls={false}
                            style={{ width: '100%' }}
                            onChange={(value) => {
                                setEditedOutputs((prev) => ({
                                    ...prev,
                                    [record.id]: value == null ? toRewardNumber(record.actual_output) : Number(value),
                                }));
                            }}
                        />
                    );
                },
            },
            {
                dataIndex: 'from_user_id',
                title: <FormattedMessage id="miningWeeklyRewards.columns.fromUserId" />,
                width: 200,
                render: (_, record) => renderCopyableId(record.from_user_id),
            },
            {
                dataIndex: 'audit_status',
                title: <FormattedMessage id="miningWeeklyRewards.columns.auditStatus" />,
                width: 100,
                render: (_, record) =>
                    isPendingStatus(record.audit_status) ? (
                        <Tag color="orange">
                            <FormattedMessage id="miningWeeklyRewards.auditStatus.pending" />
                        </Tag>
                    ) : (
                        <Tag color="green">
                            <FormattedMessage id="miningWeeklyRewards.auditStatus.audited" />
                        </Tag>
                    ),
            },
            {
                dataIndex: 'audit_by',
                title: <FormattedMessage id="miningWeeklyRewards.columns.auditBy" />,
                width: 140,
                ellipsis: true,
                render: (_, record) => record.audit_by || '-',
            },
            {
                dataIndex: 'audit_at',
                title: <FormattedMessage id="miningWeeklyRewards.columns.auditAt" />,
                width: 180,
                render: (_, record) => formatDateTime(record.audit_at),
            },
            {
                dataIndex: 'created_at',
                title: <FormattedMessage id="miningWeeklyRewards.columns.createdAt" />,
                width: 180,
                render: (_, record) => formatDateTime(record.created_at),
            },
        ],
        [canReview, editedOutputs, formatMessage],
    );

    const periodLabel =
        getWeekPeriodLabel(weekOverview) !== '-'
            ? getWeekPeriodLabel(weekOverview)
            : getRewardPeriodLabel(dataSource[0]);

    const overviewPending = weekOverview
        ? isPendingStatus(weekOverview.audit_status)
        : dataSource.some((row) => isPendingStatus(row.audit_status));

    return (
        <Permission permission={PERMISSION}>
            <ProCard
                title={
                    <Flex align="center" gap={12} wrap>
                        <Button type="link" onClick={() => navigate(LIST_PATH)} style={{ paddingInline: 0 }}>
                            <FormattedMessage id="miningWeeklyRewards.back" />
                        </Button>
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            <FormattedMessage id="miningWeeklyRewards.detailTitle" />
                            {periodLabel !== '-' ? ` – ${periodLabel}` : ''}
                        </Typography.Title>
                    </Flex>
                }
            >
                <Descriptions
                    title={<FormattedMessage id="miningWeeklyRewards.period.overview" />}
                    bordered
                    size="small"
                    column={{ xs: 1, sm: 2, md: 3 }}
                    style={{ marginBottom: 16 }}
                >
                    <Descriptions.Item label={<FormattedMessage id="miningWeeklyRewards.period.weekN" />}>
                        {hasValidWeek ? weekN : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={<FormattedMessage id="miningWeeklyRewards.period.range" />}>
                        {formatPeriodRange(weekOverview?.start_time, weekOverview?.end_time)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<FormattedMessage id="miningWeeklyRewards.period.userCount" />}>
                        {weekOverview?.record_count ?? weekOverview?.user_count ?? '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={<FormattedMessage id="miningWeeklyRewards.period.actualOutput" />}>
                        {formatOutput(weekOverview?.actual_output)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<FormattedMessage id="miningWeeklyRewards.period.status" />}>
                        {overviewPending ? (
                            <Tag color="orange">
                                <FormattedMessage id="miningWeeklyRewards.status.pending" />
                            </Tag>
                        ) : (
                            <Tag color="green">
                                <FormattedMessage id="miningWeeklyRewards.status.approved" />
                            </Tag>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label={<FormattedMessage id="miningWeeklyRewards.period.autoReleaseAt" />}>
                        {formatAutoReleaseAt(weekOverview?.end_time)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<FormattedMessage id="miningWeeklyRewards.period.approvedAt" />}>
                        {formatDateTime(weekOverview?.audit_at)}
                    </Descriptions.Item>
                </Descriptions>

                <Form form={searchForm} onFinish={handleSearch} style={{ marginBottom: 16 }}>
                    <Flex gap={16} wrap>
                        <Form.Item name="user_id" style={{ marginBottom: 0 }}>
                            <Input
                                allowClear
                                placeholder={formatMessage({
                                    id: 'miningWeeklyRewards.search.userId',
                                })}
                                style={{ width: 200 }}
                            />
                        </Form.Item>
                        <Form.Item name="reward_type" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                options={rewardTypeOptions}
                                placeholder={formatMessage({
                                    id: 'miningWeeklyRewards.search.rewardType',
                                })}
                                style={{ width: 160 }}
                            />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            <FormattedMessage id="b.search" />
                        </Button>
                        <Button onClick={handleResetSearch}>
                            <FormattedMessage id="b.reset" />
                        </Button>
                        {canExport ? (
                            <Button loading={exporting} onClick={handleExport}>
                                <FormattedMessage id="miningWeeklyRewards.actions.export" />
                            </Button>
                        ) : null}
                    </Flex>
                </Form>

                <AdminProTable<MiningWeeklyRewardItem>
                    bordered
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    rowKey="id"
                    rowSelection={rowSelection}
                    search={false}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setPagination({ current: page, pageSize: pageSize ?? 20 });
                            fetchList(page, pageSize ?? 20);
                        },
                    }}
                    toolBarRender={() => [
                        canReview ? (
                            <Typography.Text key="selected" type="secondary">
                                <FormattedMessage
                                    id="miningWeeklyRewards.audit.selected"
                                    values={{ count: selectedRowKeys.length }}
                                />
                            </Typography.Text>
                        ) : null,
                        <Permission permission={REVIEW_PERMISSION} key="batch">
                            <Button
                                disabled={!selectedRowKeys.length}
                                onClick={openBatchModal}
                            >
                                <FormattedMessage id="miningWeeklyRewards.actions.batchRelease" />
                            </Button>
                        </Permission>,
                        <Permission permission={REVIEW_PERMISSION} key="period">
                            <Button type="primary" onClick={openPeriodModal}>
                                <FormattedMessage id="miningWeeklyRewards.actions.releasePeriod" />
                            </Button>
                        </Permission>,
                    ]}
                    options={{
                        reload: () =>
                            fetchList(
                                pagination.current as number,
                                pagination.pageSize as number,
                            ),
                        density: true,
                        setting: true,
                    }}
                    scroll={{ x: 1600 }}
                    locale={{
                        emptyText: formatMessage({ id: 'miningWeeklyRewards.empty' }),
                    }}
                />
            </ProCard>

            <AuditModal
                open={modal.open}
                mode={modal.open ? modal.mode : 'batch'}
                count={selectedRowKeys.length}
                periodLabel={periodLabel}
                loading={submitting}
                onCancel={closeModal}
                onSubmit={(values) => {
                    if (!modal.open) return;
                    if (modal.mode === 'batch') {
                        return handleBatchApprove(values);
                    }
                    return handlePeriodApprove(values);
                }}
            />
        </Permission>
    );
};

export default PageMiningWeeklyRewardDetail;
